import { Lightbulb } from "lucide-react";
import { useRef, useEffect, useState } from "react";

type AIReasoningSectionProps = {
  reasoningText: string;
  isExpanded: boolean;
};

export const AIReasoningSection = ({ reasoningText, isExpanded }: AIReasoningSectionProps) => {
  const [isReasoningExpanded, setIsReasoningExpanded] = useState(false);
  const [showReadMore, setShowReadMore] = useState(false);
  const reasoningTextRef = useRef<HTMLParagraphElement>(null);

  // Collapse reasoning text when view profile is collapsed
  useEffect(() => {
    if (!isExpanded) {
      setIsReasoningExpanded(false);
    }
  }, [isExpanded]);

  // Check if text exceeds 2 lines
  useEffect(() => {
    if (!reasoningTextRef.current || !reasoningText) {
      setShowReadMore(false);
      return;
    }
    
    const checkOverflow = () => {
      const element = reasoningTextRef.current;
      if (!element) {
        setShowReadMore(false);
        return;
      }
      
      const hasLineClamp = element.classList.contains('line-clamp-2');
      if (hasLineClamp) {
        element.classList.remove('line-clamp-2');
      }
      
      const fullHeight = element.scrollHeight;
      const lineHeight = parseFloat(getComputedStyle(element).lineHeight) || 22;
      const maxHeight = lineHeight * 2;
      
      if (hasLineClamp) {
        element.classList.add('line-clamp-2');
      }
      
      if (fullHeight > maxHeight) {
        setShowReadMore(true);
      } else {
        setShowReadMore(false);
      }
    };
    
    const rafId = requestAnimationFrame(() => {
      setTimeout(checkOverflow, 0);
    });
    
    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [reasoningText]);

  return (
    <div className="flex items-start gap-3 pt-1.5">
      <div className="relative group flex items-start justify-start h-10 w-10 shrink-0">
        <Lightbulb className="h-8 w-8 text-[#026857]" aria-hidden="true" />
        <div className="pointer-events-none absolute left-1/2 top-full mt-2 -translate-x-1/2 translate-y-1 opacity-0 transition-all duration-200 group-hover:translate-y-0 group-hover:opacity-100">
          <div className="rounded-xl border border-[#E5E7EB] bg-white px-3 py-1.5 shadow-xl whitespace-nowrap text-xs font-termina capitalize text-[#111827]">
            AI Reasoning
          </div>
        </div>
      </div>
      <div className="flex-1">
        <p 
          ref={reasoningTextRef}
          className={`text-sm font-termina text-[#4B5563] leading-relaxed ${
            !isReasoningExpanded && showReadMore ? 'line-clamp-2' : ''
          }`}
        >
          {reasoningText}
        </p>
        {showReadMore && (
          <button
            type="button"
            onClick={() => setIsReasoningExpanded(!isReasoningExpanded)}
            className="mt-1 text-sm font-termina text-[#026857] hover:text-[#025247] transition-colors"
          >
            {isReasoningExpanded ? 'Read less' : 'Read more'}
          </button>
        )}
      </div>
    </div>
  );
};

